import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { gql, useApolloClient, useLazyQuery } from '@apollo/client';
import {
  Button,
  CoinsIcon,
  DateRangePicker,
  H2,
  InfoBlock,
  ITabOption,
  Pagination,
  Paragraph,
  Table,
  Tabs,
  WalletArrowLeftIcon,
  WalletArrowRightIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Column } from 'react-table';

import { ClientSuggest } from 'components';
import { useSharedDateRange, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { DepositModal } from 'modals';
import { SettingsContext } from 'settings';
import { TClient } from 'types';
import { formatDateForServer } from 'utils';

import {
  ETransfersType,
  IGetTransfersReportCSVResponse,
  IGetTransfersReportRequest,
  IGetTransfersReportResponse,
  IGetTransfersSummaryReportRequest,
  IGetTransfersSummaryReportResponse,
  TNormalizedTransfersSummaryReportData,
  TTransfersData,
} from './interfaces';
import {
  DEPOSIT_TRANSFERS_REPORT,
  DEPOSIT_TRANSFERS_REPORT_CSV,
  DEPOSIT_TRANSFERS_SUMMARY_REPORT,
} from './schema';
import s from './Transfers.module.scss';
import { getTransfersColumns } from './utils';
import { getTransfersSummaryColumns } from './utils/getTransfersSummaryColumns';

export const Transfers: FC = () => {
  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);
  const { t, lang } = useContext(LocalizationContext);

  const apolloClient = useApolloClient();

  const today = dayjs();
  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();

  const [tab, setTab] = useState<undefined | ETransfersType>(undefined);
  const [search, setSearch] = useState<string | null>();
  const [pagesInfo, setPagesInfo] = useState({ currentPage: 1, lastPage: 1 });
  const [error, setError] = useState<string | null>(null);
  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');

  const { lastPage, currentPage } = pagesInfo;

  const [generateReport, { data: reportData, loading, variables }] =
    useLazyQuery<IGetTransfersReportResponse, IGetTransfersReportRequest>(
      DEPOSIT_TRANSFERS_REPORT,
      {
        fetchPolicy: 'no-cache',
        onCompleted: (response) => {
          if (!response || !response.depositTransferReport) return;

          setPagesInfo({ ...response.depositTransferReport.paginatorInfo });
        },
        onError: (error) => {
          setError(error.message);
        },
      }
    );

  const [
    generateSummaryReport,
    { data: summaryReportData, loading: summaryReportLoading },
  ] = useLazyQuery<
    IGetTransfersSummaryReportResponse,
    IGetTransfersSummaryReportRequest
  >(DEPOSIT_TRANSFERS_SUMMARY_REPORT, {
    fetchPolicy: 'no-cache',
  });

  const [generateReportFile, { loading: loadingFile }] = useLazyQuery<
    IGetTransfersReportCSVResponse,
    IGetTransfersReportRequest
  >(DEPOSIT_TRANSFERS_REPORT_CSV, {
    onCompleted: (response) => {
      if (!response) return;

      open(response.depositTransferReport.url, '_self');
    },
    fetchPolicy: 'no-cache',
  });

  const NormalizedSummaryReportData = useMemo<
    TNormalizedTransfersSummaryReportData[]
  >(() => {
    if (!summaryReportData || !summaryReportData.depositTransferSummaryReport) {
      return [] as TNormalizedTransfersSummaryReportData[];
    }

    const {
      depositTransferSummaryReport: { data, extraLabels },
    } = summaryReportData;

    return data.map(
      ({ values }, index): TNormalizedTransfersSummaryReportData => ({
        name: extraLabels[index] || '',
        input: values[0] || 0,
        output: values[1] || 0,
        remains: values[2],
      })
    );
  }, [summaryReportData]);

  const COLUMNS = useMemo<{
    transfersColumns: Array<Column<TTransfersData>>;
    transfersSummaryColumns: Array<
      Column<TNormalizedTransfersSummaryReportData>
    >;
  }>(() => {
    return {
      transfersColumns: getTransfersColumns(t, currency),
      transfersSummaryColumns: getTransfersSummaryColumns(t, currency),
    };
  }, [t, currency]);

  const TABS = useMemo<Array<ITabOption<ETransfersType | undefined>>>(
    () => [
      {
        title: t('transfers.all'),
        value: undefined,
      },
      {
        title: t('withdraw'),
        value: ETransfersType.WITHDRAW,
      },
      {
        title: t('renewal'),
        value: ETransfersType.DEPOSIT,
      },
    ],
    [t]
  );

  useEffect(() => {
    if (!variables || JSON.stringify(variables) === '{}') return;

    const input = { ...variables.input, type: tab };

    if (!search) {
      input.search = undefined;
    } else {
      const selectedClient = apolloClient.readFragment<Pick<TClient, 'phone'>>({
        id: apolloClient.cache.identify({
          __typename: 'User',
          uuid: search,
        }),
        fragment: gql`
          fragment ClientPhone on User {
            phone
          }
        `,
      });

      if (selectedClient?.phone) {
        input.search = { q: selectedClient.phone };
      }
    }

    generateReport({
      variables: {
        ...variables,
        input,
        page: 1,
      },
    });
  }, [tab, search]);

  const size = useWindowResize();

  return (
    <div className={s.container}>
      {isDepositOpen && (
        <DepositModal
          defaultClientUUID={defaultClient}
          onClose={() => {
            setIsDepositOpen(false);
          }}
        />
      )}
      {error && (
        <InfoBlock
          className={s.info_error}
          title={t('errors.error')}
          message={error}
          direction="row"
        />
      )}
      <div className={s.controls}>
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          lastValidDate={today.format()}
          firstValidDate={dayjs('2022-03-01T00:00:00').format()}
          lang={lang}
          periodType={periodType}
          setPeriodType={setPeriodType}
          offset={offset}
          setOffset={setOffset}
        />

        <div className={s.buttons_block}>
          <Button
            onClick={() => {
              generateReport({
                variables: {
                  input: {
                    ...variables?.input,
                    from: formatDateForServer(dayjs(startDate)),
                    to: formatDateForServer(dayjs(endDate)),
                    type: tab,
                  },
                  first: 50,
                  page: 1,
                },
              });

              generateSummaryReport({
                variables: {
                  input: {
                    start: formatDateForServer(dayjs(startDate)),
                    end: formatDateForServer(dayjs(endDate)),
                  },
                },
              });
            }}
            disabled={loading}
          >
            {t('reports.generate')}
          </Button>
          <Button
            className={s.export}
            iconName="IncomeIcon"
            variant="outline"
            isLoading={loadingFile}
            onClick={() => {
              if (!variables) return;

              return generateReportFile({
                variables: {
                  input: { ...variables?.input },
                },
              });
            }}
            disabled={!variables || JSON.stringify(variables) === '{}'}
          >
            {size.width < 1864 && size.width > 960 ? t('csv') : t('export.csv')}
          </Button>
        </div>
      </div>

      <div>
        <H2>{t('report.sales.title')}</H2>

        <div className={s.info}>
          <div className={s.info__block}>
            <div className={s.info__icon}>
              <WalletArrowLeftIcon color="green" size="m" />
            </div>
            <div className={s.info__texts}>
              <div className={s.info__header}>
                <H2>
                  {summaryReportData?.depositTransferSummaryReport.summary
                    ? `${summaryReportData.depositTransferSummaryReport.summary[0].value} ${currency}`
                    : '—'}
                </H2>
                {summaryReportData?.depositTransferSummaryReport.summary && (
                  <Paragraph size="s" className={s.info__mark}>
                    {`${
                      summaryReportData.depositTransferSummaryReport.summary[1]
                        .value
                    } ${t('contracted.persons')}`}
                  </Paragraph>
                )}
              </div>
              <Paragraph size="s" color="secondary" bold>
                {t('transfers.receipts')}
              </Paragraph>
            </div>
          </div>

          <div className={s.info__block}>
            <div className={cn(s.info__icon, s.info__icon_secondary)}>
              <WalletArrowRightIcon color="primary" size="m" />
            </div>
            <div className={s.info__texts}>
              <div className={s.info__header}>
                <H2>
                  {summaryReportData?.depositTransferSummaryReport.summary
                    ? `${summaryReportData.depositTransferSummaryReport.summary[2].value} ${currency}`
                    : '—'}
                </H2>
                {summaryReportData?.depositTransferSummaryReport.summary && (
                  <Paragraph size="s" className={s.info__mark}>
                    {`${
                      summaryReportData.depositTransferSummaryReport.summary[3]
                        .value
                    } ${t('contracted.persons')}`}
                  </Paragraph>
                )}
              </div>
              <Paragraph size="s" color="secondary" bold>
                {t('transfers.withdrawals')}
              </Paragraph>
            </div>
          </div>

          <div className={s.info__block}>
            <div className={cn(s.info__icon, s.info__icon_secondary)}>
              <CoinsIcon color="primary" size="m" />
            </div>
            <div className={s.info__texts}>
              <div className={s.info__header}>
                <H2>
                  {summaryReportData?.depositTransferSummaryReport.summary
                    ? `${summaryReportData.depositTransferSummaryReport.summary[4].value} ${currency}`
                    : '—'}
                </H2>
              </div>
              <Paragraph size="s" color="secondary" bold>
                {t('total')}
              </Paragraph>
            </div>
          </div>
        </div>

        <Table
          data={NormalizedSummaryReportData}
          columns={COLUMNS.transfersSummaryColumns}
          className={s.table_summary}
          emptyDataPlaceholder={
            !NormalizedSummaryReportData
              ? t('transfers.notData.placeholder')
              : t('transfers.emptyData.placeholder')
          }
          isLoading={summaryReportLoading}
        />
      </div>

      <div className={s.operation}>
        <div className={s.operation_top}>
          <ClientSuggest
            value={search || ''}
            onChange={(value) => setSearch(value)}
            isButtonClear
            disabled={Boolean(search)}
            showBorder={size.width < 960}
            withReplenishment
            setIsDefaultClient={setIsDefaultClient}
            setDepositOpen={setIsDepositOpen}
          />
          <Tabs
            options={TABS}
            onChange={setTab}
            value={tab}
            className={s.tabs}
          />
        </div>

        <Table
          data={reportData?.depositTransferReport?.data || []}
          columns={COLUMNS.transfersColumns}
          className={cn(
            s.table,
            (!reportData || !reportData.depositTransferReport.data.length) &&
              s.table_notDate
          )}
          emptyDataPlaceholder={
            !reportData
              ? t('transfers.notData.placeholder')
              : t('transfers.emptyData.placeholder')
          }
          isLoading={loading}
        />
      </div>
      {lastPage > 1 && (
        <Pagination
          onChange={(newPage) => {
            if (!variables || JSON.stringify(variables) === '{}') return;

            return generateReport({
              variables: { ...variables, page: newPage },
            });
          }}
          lastPage={lastPage}
          currentPage={currentPage}
          className={s.pagination}
        />
      )}
    </div>
  );
};
